import { useCallback, useEffect, useState } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, FileText, Calendar, Truck, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import type { Shipment } from '../types'
import { formatDate, getShipmentStatusLabel, getShipmentStatusColor } from '../utils/helpers'

export default function ShipmentDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const erpId = searchParams.get('erp_id') ? Number(searchParams.get('erp_id')) : undefined
  const [shipment, setShipment] = useState<Shipment | null>(null)
  const [loading, setLoading] = useState(true)

  const loadShipment = useCallback(async () => {
    if (!id) return
    try {
      const data = await api.getShipment(id, erpId)
      setShipment(data)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      console.error('Error loading shipment:', apiErr)
    } finally {
      setLoading(false)
    }
  }, [id, erpId])

  useEffect(() => {
    loadShipment()
  }, [loadShipment])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!shipment) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('shipmentDetail.notFound')}</h2>
        <Link to="/shipments" className="text-primary-600 hover:text-primary-700">
          {t('shipmentDetail.backToList')}
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/shipments" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('shipmentDetail.title', { ref: shipment.ref })}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {t('shipmentDetail.createdOn', { date: formatDate(shipment.date_creation) })}
            </p>
          </div>
        </div>
      </div>

      {/* Status Badge */}
      <div>
        <span
          className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${getShipmentStatusColor(shipment.status)}`}
        >
          {getShipmentStatusLabel(shipment.status, t)}
        </span>
      </div>

      {/* Main Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{t('shipmentDetail.generalInfo')}</h2>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Date de création */}
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('shipmentDetail.creationDate')}</p>
              <p className="font-medium text-gray-900">{formatDate(shipment.date_creation)}</p>
            </div>
          </div>

          {/* Date de livraison */}
          {shipment.delivery_date && (
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('shipmentDetail.deliveryDate')}</p>
                <p className="font-medium text-gray-900">{formatDate(shipment.delivery_date)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tracking */}
      {shipment.tracking_number && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('shipmentDetail.trackingTitle')}
            </h2>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3">
              <Truck className="w-5 h-5 text-gray-500" />
              <span className="font-mono text-gray-900">{shipment.tracking_number}</span>
              {shipment.tracking_url && (
                <a
                  href={shipment.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t('shipmentDetail.trackPackage')}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      {shipment.notes && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">{t('shipmentDetail.notes')}</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-700 whitespace-pre-wrap">{shipment.notes}</p>
          </div>
        </div>
      )}
    </div>
  )
}
